import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWeatherHistoryQuery } from "../../../api/network/weatherApiServices";
import { WeatherHistoryObservation } from "../../../api/types/Weather";
import FTModal from "../../../components/common/FTModal";
import svgIcons from "../../../assets/svg/svgIcons";
import getWeatherIconPath from "../../../assets/weather_icons";
import { useAccountTimezone } from "../../../hooks/timezone";
import { dbTimeToJsTime, formattedDateTimeBasedOnTimezone } from "../../../utils/dateTime";
import { convertKmphTo } from "../../../utils/distance";
import { useLoggedInUserData } from "../../../utils/user";
import {
  buildTenMinuteDisplayTimeline,
  calculateHeatIndexCelsius,
  closestWeatherObservation,
  formatHodDateTime,
  pressureTrendLabel,
  unpackWeatherHistory,
  windDegreesToCompass,
} from "../../../utils/weatherHistory";
import { SPEED_UNIT_VALUES, SPEED_UNITS_OPTIONS } from "../../admin-details/account/constants";
import { IncidentDetailResponse } from "./type";
import { APP_CONFIG } from "../../../constants/constants";

const NOT_AVAILABLE = "Not Available";
const celsiusToFahrenheit = (celsius: number): number => (celsius * 9) / 5 + 32;

type IncidentDetailWeatherProps = {
  incident: IncidentDetailResponse;
  temperatureUnit: "C" | "F";
  onValueChange?: (value: string) => void;
  printMode?: boolean;
  onLoadComplete?: () => void;
  onTemperatureUnitChange?: (unit: "C" | "F") => void;
};

const IncidentDetailWeather: FC<IncidentDetailWeatherProps> = (
  { incident, temperatureUnit, printMode, onLoadComplete, onTemperatureUnitChange }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage.widget_weather",
  });
  const { convertToTZForDateFns, tzForDateFns } = useAccountTimezone();
  const [showDetail, setShowDetail] = useState(false);

  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedOption =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit) ?? SPEED_UNITS_OPTIONS[0];

  const hasLocation = !!incident.latitude && !!incident.longitude;
  const startDateTime = formatHodDateTime((incident.timestamp - 10800) * 1000); // 3 hours before
  const endDateTime = formatHodDateTime(incident.timestamp * 1000);
  const hasCalledLoadComplete = useRef(false);

  const { data, isFetching, isError } = useWeatherHistoryQuery(
    {
      lat: incident.latitude,
      lon: incident.longitude,
      startDateTime,
      endDateTime,
      units: "m",
    },
    { skip: !hasLocation }
  );

  const observations = useMemo(() => unpackWeatherHistory(data), [data]);
  const closest = useMemo(
    () => closestWeatherObservation(observations, (incident.timestamp - 300) * 1000),
    [observations, incident.timestamp]
  );

  const timelineSlots = useMemo(
    () => buildTenMinuteDisplayTimeline(observations, incident.timestamp * 1000),
    [observations, incident.timestamp]
  );

  const formatTemp = (celsius: number | undefined) =>
    celsius === undefined || celsius === null || isNaN(celsius)
      ? NOT_AVAILABLE
      : `${Math.round(temperatureUnit === "F" ? celsiusToFahrenheit(celsius) : celsius)}°${temperatureUnit}`;

  const formatWindSpeed = (kph: number | undefined) =>
    kph === undefined || kph === null || isNaN(kph)
      ? NOT_AVAILABLE
      : `${Math.round(convertKmphTo(speedOption.unit, kph))} ${speedOption.label}`;

  const incidentDateTime = formattedDateTimeBasedOnTimezone(
    dbTimeToJsTime(incident.timestamp),
    convertToTZForDateFns(tzForDateFns),
    "LLLL dd, yyyy hh:mmaa"
  );
  const handleTemperatureUnitChange = (unit: "C" | "F") => {
    onTemperatureUnitChange?.(unit);
  };
  useEffect(() => {
    if (
      printMode &&
      !isFetching &&
      !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== INCIDENT DETAIL WEATHER LOADED",
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetching, onLoadComplete]);

  if (printMode) {
    return (
      <div className="print:[break-before:page] print:[page-break-before:always]">
        <div className="printable-landscape-page bg-white rounded-xl border border-heading-gray888/20 shadow-sm p-4">
          <h2 className={`font-semibold text-heading-black ${printMode ? "text-xl pb-4" : "text-[15px]"}`}>{t("heading")}</h2>
          {closest &&
            <WeatherDetailModal
              incident={incident}
              closest={closest}
              timelineSlots={timelineSlots}
              temperatureUnit={temperatureUnit}
              setTemperatureUnit={handleTemperatureUnitChange}
              formatTemp={formatTemp}
              formatWindSpeed={formatWindSpeed}
              printMode
              onHide={() => setShowDetail(false)}
            />
          }
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-xl border border-heading-gray888/20 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-[15px] text-heading-black">{t("heading")}</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDetail(true)}
            disabled={!hasLocation || isFetching || isError || !closest}
            className="h-7 px-3 rounded-md border border-accent-blue-dark text-accent-blue-dark text-xs font-semibold enabled:hover:bg-accent-blue-pale transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("view_detail")}
          </button>
        </div>
      </div>

      {!hasLocation ? (
        <div className="h-24 rounded-lg bg-gray-100 flex items-center justify-center">
          <p className="text-heading-gray666 text-sm px-8 text-center">{NOT_AVAILABLE}</p>
        </div>
      ) : isFetching ? (
        <div className="h-24 flex items-center justify-center">
          <img src={svgIcons.LoadingAnimation} alt="" className="size-8" />
        </div>
      ) : isError || !closest ? (
        <div className="h-24 rounded-lg bg-gray-100 flex items-center justify-center">
          <p className="text-heading-gray666 text-sm px-8 text-center">{t("fetch_error")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <img src={getWeatherIconPath(`${closest.iconCode}` as any)} alt="" className="size-11 shrink-0" />
            <span className="text-[28px] leading-9 font-bold text-heading-black shrink-0">
              {formatTemp(closest.temperature)}
            </span>
            <div className="flex flex-col gap-1 text-xs text-heading-gray666 pt-0.5 pl-1">
              <span>
                {t("field_precipitation")}: <span className="text-accent-blue-dark font-semibold">{closest.precip1Hour != null ? `${closest.precip1Hour.toFixed(1)}mm` : NOT_AVAILABLE}</span>
                <span className="mx-1.5 text-heading-gray888/60">•</span>
                {t("field_wind_speed")}: <span className="text-accent-blue-dark font-semibold">{formatWindSpeed(closest.windSpeed)}</span>
              </span>
              <span>
                {t("field_uv_index")}: <span className="text-accent-blue-dark font-semibold">{closest.uvIndex != null ? `${closest.uvIndex}` : NOT_AVAILABLE}</span>
                <span className="mx-1.5 text-heading-gray888/60">•</span>
                {t("field_humidity")}: <span className="text-accent-blue-dark font-semibold">{closest.relativeHumidity != null ? `${Math.round(closest.relativeHumidity)}%` : NOT_AVAILABLE}</span>
              </span>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-semibold tracking-wider text-heading-gray666 uppercase">{t("location_from_gps")}</p>
            <div className="grid grid-cols-[3.5rem_1fr] gap-y-0.5 text-sm mt-1.5">
              <span className="text-heading-gray666">{t("lat")}</span>
              <span className="font-semibold text-heading-black">{incident.latitude}°</span>
              <span className="text-heading-gray666">{t("long")}</span>
              <span className="font-semibold text-heading-black">{incident.longitude}°</span>
            </div>
          </div>

          <div>
            <p className="text-[10px] font-semibold tracking-wider text-heading-gray666 uppercase">{t("event_date_time")}</p>
            <p className="text-sm font-semibold text-heading-black mt-1.5">{incidentDateTime}</p>
          </div>
        </div>
      )}

      {(showDetail && closest) && (
        <WeatherDetailModal
          onHide={() => setShowDetail(false)}
          incident={incident}
          closest={closest}
          timelineSlots={timelineSlots}
          temperatureUnit={temperatureUnit}
          setTemperatureUnit={handleTemperatureUnitChange}
          formatTemp={formatTemp}
          formatWindSpeed={formatWindSpeed}
        />
      )}
    </div>
  );
};

type WeatherDetailModalProps = {
  onHide: () => void;
  incident: IncidentDetailResponse;
  closest: WeatherHistoryObservation;
  timelineSlots: ReturnType<typeof buildTenMinuteDisplayTimeline>;
  temperatureUnit: "C" | "F";
  setTemperatureUnit: (unit: "C" | "F") => void;
  formatTemp: (celsius: number | undefined) => string;
  formatWindSpeed: (kph: number | undefined) => string;
  printMode?: boolean;
};

const WeatherDetailModal: FC<WeatherDetailModalProps> = ({
  onHide,
  incident,
  closest,
  timelineSlots,
  temperatureUnit,
  setTemperatureUnit,
  formatTemp,
  formatWindSpeed,
  printMode = false,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage.widget_weather",
  });
  const { convertToTZForDateFns, tzForDateFns } = useAccountTimezone();

  const fieldRows: { label: string; format: (obs: WeatherHistoryObservation) => string }[] = [
    { label: t("field_temperature"), format: (obs) => formatTemp(obs.temperature) },
    { label: t("field_feels_like"), format: (obs) => formatTemp(obs.temperatureFeelsLike) },
    {
      label: t("field_precipitation"),
      format: (obs) => (obs.precip1Hour != null ? `${obs.precip1Hour.toFixed(1)} mm` : NOT_AVAILABLE),
    },
    { label: t("field_wind_speed"), format: (obs) => formatWindSpeed(obs.windSpeed) },
    {
      label: t("field_wind_direction"),
      format: (obs) => `${Math.round(obs.windDirection)}° (${windDegreesToCompass(obs.windDirection)})`,
    },
    {
      label: t("field_humidity"),
      format: (obs) => (obs.relativeHumidity != null ? `${Math.round(obs.relativeHumidity)}%` : NOT_AVAILABLE),
    },
    {
      label: t("field_visibility"),
      format: (obs) => (obs.visibility != null ? `${obs.visibility.toFixed(1)} km` : NOT_AVAILABLE),
    },
    {
      label: t("field_pressure"),
      format: (obs) => (obs.pressureMeanSeaLevel != null ? `${obs.pressureMeanSeaLevel.toFixed(1)} hPa` : NOT_AVAILABLE),
    },
    { label: t("field_pressure_trend"), format: (obs) => pressureTrendLabel(obs.pressureChange) },
    { label: t("field_uv_index"), format: (obs) => (obs.uvIndex != null ? `${obs.uvIndex}` : NOT_AVAILABLE) },
    {
      label: t("field_heat_index"),
      format: (obs) => formatTemp(calculateHeatIndexCelsius(obs.temperature, obs.relativeHumidity)),
    },
    { label: t("field_cloud_ceiling"), format: () => NOT_AVAILABLE },
  ];

  const modalContent = (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-stretch gap-y-4">
        <div className="flex items-start gap-3 pr-6">
          <img src={getWeatherIconPath(`${closest.iconCode}` as any)} alt="" className="size-12 shrink-0" />
          <div>
            <p className="text-2xl font-bold text-heading-black leading-7">{formatTemp(closest.temperature)}</p>
            <div className="flex flex-col gap-0.5 text-xs text-heading-gray666 mt-1.5">
              <span>
                {t("field_precipitation")}: <span className="text-accent-blue-dark font-semibold">{closest.precip1Hour != null ? `${closest.precip1Hour.toFixed(1)}mm` : NOT_AVAILABLE}</span>
                <span className="mx-1.5 text-heading-gray888/60">•</span>
                {t("field_wind_speed")}: <span className="text-accent-blue-dark font-semibold">{formatWindSpeed(closest.windSpeed)}</span>
              </span>
              <span>
                {t("field_uv_index")}: <span className="text-accent-blue-dark font-semibold">{closest.uvIndex ?? NOT_AVAILABLE}</span>
                <span className="mx-1.5 text-heading-gray888/60">•</span>
                {t("field_humidity")}: <span className="text-accent-blue-dark font-semibold">{closest.relativeHumidity != null ? `${Math.round(closest.relativeHumidity)}%` : NOT_AVAILABLE}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="border-l border-heading-gray888/20 px-6">
          <p className="text-[10px] font-semibold tracking-wider text-heading-gray666 uppercase">{t("location_from_gps")}</p>
          <div className="grid grid-cols-[3.5rem_1fr] gap-y-0.5 text-sm mt-1.5">
            <span className="text-heading-gray666">{t("lat")}</span>
            <span className="font-semibold text-heading-black">{incident.latitude}°</span>
            <span className="text-heading-gray666">{t("long")}</span>
            <span className="font-semibold text-heading-black">{incident.longitude}°</span>
          </div>
        </div>

        <div className="border-l border-heading-gray888/20 px-6">
          <p className="text-[10px] font-semibold tracking-wider text-heading-gray666 uppercase">{t("event_date_time")}</p>
          <div className="grid grid-cols-[3.5rem_1fr] gap-y-0.5 text-sm mt-1.5">
            <span className="text-heading-gray666">{t("date_label")}</span>
            <span className="font-semibold text-heading-black">
              {formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(incident.timestamp),
                convertToTZForDateFns(tzForDateFns),
                "LLLL dd, yyyy"
              )}
            </span>
            <span className="text-heading-gray666">{t("time_label")}</span>
              <span className="font-semibold text-heading-black">
              {formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(incident.timestamp),
                convertToTZForDateFns(tzForDateFns),
                "hh:mm aa"
              )}
            </span>
          </div>
        </div>

        <label htmlFor="weather-temp-unit" className="flex gap-1.5 items-center cursor-pointer text-xs text-heading-gray666 ml-auto self-start">
          <span>C</span>
          <input
            id="weather-temp-unit"
            type="checkbox"
            checked={temperatureUnit === "F"}
            onChange={(e) => setTemperatureUnit(e.target.checked ? "F" : "C")}
            className="sr-only peer"
          />
          <div className="relative w-8 h-4 bg-gray-200 rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-accent-blue-dark" />
          <span>F</span>
        </label>
      </div>

      <div className="rounded-xl border border-heading-gray888/20 p-5">
        <p className="text-[11px] font-semibold tracking-wider text-heading-gray666 uppercase mb-8">{t("history_hint")}</p>

        <div className="grid" style={{ gridTemplateColumns: `9rem repeat(${timelineSlots.length}, minmax(0, 1fr))` }}>
          <div />
          {timelineSlots.map((slot, i) => {
            const isIncidentTime = i === timelineSlots.length - 1;
            return (
              <div key={slot.targetEpochMs} className="flex flex-col items-center gap-1.5">
                <span className={`h-5 flex items-center px-1.5 rounded text-[9px] font-semibold tracking-wide uppercase ${isIncidentTime ? "bg-accent-blue-pale text-accent-blue-dark" : "invisible"}`}>
                  {t("incident_time")}
                </span>
                <span className="text-xs font-semibold text-heading-black">
                  {formattedDateTimeBasedOnTimezone(slot.targetEpochMs, convertToTZForDateFns(tzForDateFns), "hh:mm aa")}
                </span>
                <div className="relative w-full flex justify-center items-center h-4">
                  {i > 0 && (
                    <span className={`absolute right-1/2 left-[-50%] top-1/2 -translate-y-1/2 h-0.5 ${isIncidentTime ? "bg-amber-400" : "bg-heading-gray888/30"}`} />
                  )}
                  <span className={`relative z-10 size-2.5 rounded-full border-2 ${isIncidentTime ? "bg-amber-400 border-amber-400" : "bg-white border-heading-gray888/50"}`} />
                </div>
                <img src={getWeatherIconPath(`${slot.observation.iconCode}` as any)} alt="" className="size-7 mt-1" />
                <span className="text-xs text-heading-gray666">{formatTemp(slot.observation.temperature)}</span>
              </div>
            );
          })}
        </div>

        <div className="overflow-x-auto mt-4">
          <table className="w-full text-[13px] table-fixed">
            <tbody>
              {fieldRows.map((row) => (
                <tr key={row.label} className="border-t border-heading-gray888/15">
                  <td className="py-3.5 pr-4 text-heading-gray666 whitespace-nowrap w-36">{row.label}</td>
                  {timelineSlots.map((slot) => (
                    <td key={slot.targetEpochMs} className="py-3.5 px-2 font-semibold text-heading-black text-center whitespace-nowrap">
                      {row.format(slot.observation)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (printMode) {
    return modalContent;
  }
  return (
    <FTModal visible={true} onHide={onHide} heading={t("heading")} position="center" containerStyle="max-w-[calc(100vw-4rem)] w-[760px]">
      {modalContent}
    </FTModal>
  );
};

export default IncidentDetailWeather;
